import { http } from "./client";

// Attendance (HR / managers): days with check-in sessions, validation of
// sessions, HR edits, check-in settings and biometrics import.
// Every function returns the response body.
const data = (r) => r.data;

/** Drop empty filters so they aren't sent as `status=` etc. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

export const attendanceApi = {
  /**
   * GET /attendance → { data: Day[] (with employee, sessions, sessionCount, forReviewCount,
   *   hasLocation, outsideBranch), total, page, limit, from, to }
   * filters: from, to (default this month), branchId, search, employeeId, status,
   *          sessionStatus (e.g. FOR_REVIEW), outsideOnly, page, limit
   */
  getList: (filters = {}) =>
    http.get("/attendance", { params: clean(filters) }).then(data),
  // GET /attendance/:id → day with sessions
  getById: (id) => http.get(`/attendance/${id}`).then(data),
  // GET /attendance/:id/locations → { attendanceId, date, employee, branch, sessions }
  getLocations: (id) => http.get(`/attendance/${id}/locations`).then(data),

  /* ---------- Validation of check-in periods ---------- */
  // GET /attendance/sessions/for-review → { data: (Session & { employee })[], total, page, limit } (never my own)
  getForReview: (filters = {}) =>
    http
      .get("/attendance/sessions/for-review", { params: clean(filters) })
      .then(data),
  // POST /attendance/sessions/:id/review { action: APPROVE | REJECT, remarks? (required for REJECT) } → day
  reviewSession: (id, { action, remarks }) =>
    http
      .post(`/attendance/sessions/${id}/review`, clean({ action, remarks }))
      .then(data),
  // POST /attendance/sessions/review { sessionIds?, attendanceIds?, action, remarks? } → { updated, days, status }
  // (attendanceIds confirms whole days, incl. old records whose period has no id)
  reviewSessionsBulk: (body) =>
    http.post("/attendance/sessions/review", clean(body)).then(data),
  // GET /attendance/location-flags → sessions outside the branch radius
  getLocationFlags: (filters = {}) =>
    http
      .get("/attendance/location-flags", { params: clean(filters) })
      .then(data),

  /* ---------- HR edits (refused for deployed employees, own record, locked DTR months) ---------- */
  // POST /attendance { employeeId, date, checkInTime?, checkOutTime?, status?, remarks? } → manual day
  createDay: (body) => http.post("/attendance", clean(body)).then(data),
  // POST /attendance/:id/sessions { checkInTime, checkOutTime?, remarks? }
  addSession: (attendanceId, body) =>
    http.post(`/attendance/${attendanceId}/sessions`, clean(body)).then(data),
  // PATCH /attendance/sessions/:id { checkInTime?, checkOutTime? (null clears), remarks? }
  editSession: (sessionId, body) =>
    http.patch(`/attendance/sessions/${sessionId}`, body).then(data),
  // PATCH /attendance/:id { status?, remarks? } (ON_LEAVE / HALF_DAY stick; others are recalculated)
  updateDay: (id, body) =>
    http.patch(`/attendance/${id}`, clean(body)).then(data),

  /* ---------- Check-in settings ---------- */
  // GET /attendance/settings/employees?tag=anywhere|multiple|blocked&branchId&search&page&limit
  getEmployeeSettings: (filters = {}) =>
    http
      .get("/attendance/settings/employees", { params: clean(filters) })
      .then(data),
  // PATCH /attendance/settings/employees { employeeIds, checkInAnywhere?, multipleCheckIns? } (each INHERIT|ALLOW|BLOCK)
  updateEmployeeSettings: (body) =>
    http.patch("/attendance/settings/employees", body).then(data),
  // GET /attendance/settings/roles → [{ id, title, companyId, allowMultipleCheckIns, canCheckInAnywhere }]
  getRoleSettings: () => http.get("/attendance/settings/roles").then(data),
  // PATCH /attendance/settings/roles/:roleId { allowMultipleCheckIns?, canCheckInAnywhere? } (only what changes)
  updateRoleSetting: (roleId, changes) =>
    http.patch(`/attendance/settings/roles/${roleId}`, changes).then(data),

  /* ---------- Biometrics ---------- */
  /**
   * POST /attendance/biometrics/import?dryRun&templateId|preset&linkSuggested (multipart `file`, max 10 MB).
   * No template/preset → the company's default layout (else the punch-list preset).
   * → { template, totalRows, readable, failed, employees, days, period, unmatched[], linkedNow, errors[], … }
   */
  // `config` (unsaved layout, dry run only) overrides templateId/preset; fields go before the file part
  importBiometrics: ({
    file,
    dryRun,
    templateId,
    preset,
    linkSuggested,
    config,
    system,
  }) => {
    const form = new FormData();
    if (config) form.append("config", JSON.stringify(config));
    if (config && system) form.append("system", system);
    form.append("file", file);
    return http
      .post("/attendance/biometrics/import", form, {
        params: clean({
          dryRun: dryRun ? "true" : "false",
          templateId,
          preset,
          linkSuggested: linkSuggested ? "true" : undefined,
        }),
      })
      .then(data);
  },
  // Layouts ("templates") that tell the server how to read a device export
  getBiometricLayouts: () =>
    http.get("/attendance/biometrics/templates").then(data),
  createBiometricLayout: (body) =>
    http.post("/attendance/biometrics/templates", body).then(data),
  updateBiometricLayout: (id, body) =>
    http.patch(`/attendance/biometrics/templates/${id}`, body).then(data),
  deleteBiometricLayout: (id) =>
    http.delete(`/attendance/biometrics/templates/${id}`).then(() => id),
  // [{ key, name, description, config }]
  getBiometricPresets: () =>
    http.get("/attendance/biometrics/presets").then(data),
  // Sample file → { sheets, sheet, totalRows, columns, rows (first 40), detectedLayout, suggestedPreset }
  inspectBiometrics: (file, sheet) => {
    const form = new FormData();
    form.append("file", file);
    return http
      .post("/attendance/biometrics/inspect", form, {
        params: clean({ sheet }),
      })
      .then(data);
  },
  // [{ system, linkedEmployees, templates }] (always includes "default")
  getBiometricSystems: () =>
    http.get("/attendance/biometrics/systems").then(data),
  // Device IDs per employee: { system, data: [{ employeeId, employeeNumber, name, externalId }], total }
  getBiometricLinks: (params = {}) =>
    http
      .get("/attendance/biometrics/links", { params: clean(params) })
      .then(data),
  // { system, links: [{ employeeId, externalId | null }] } (409 = ID already used)
  saveBiometricLinks: (body) =>
    http.put("/attendance/biometrics/links", body).then(data),
  getBiometricImports: (params = {}) =>
    http
      .get("/attendance/biometrics/imports", { params: clean(params) })
      .then(data),
  getBiometricImport: (id) =>
    http.get(`/attendance/biometrics/imports/${id}`).then(data),
  // CSV template as a Blob (downloaded by the page)
  getBiometricTemplate: () =>
    http
      .get("/attendance/biometrics/template", { responseType: "blob" })
      .then(data),
};
